import { useState, useEffect } from 'react';
import { useReadContract } from 'wagmi';
import { formatUnits } from 'viem';

// Chainlink Price Feed ABI - tik reikalingos funkcijos
const priceFeedAbi = [
  {
    inputs: [],
    name: 'latestRoundData',
    outputs: [
      { internalType: 'uint80', name: 'roundId', type: 'uint80' },
      { internalType: 'int256', name: 'answer', type: 'int256' },
      { internalType: 'uint256', name: 'startedAt', type: 'uint256' },
      { internalType: 'uint256', name: 'updatedAt', type: 'uint256' },
      { internalType: 'uint80', name: 'answeredInRound', type: 'uint80' }
    ],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'decimals',
    outputs: [{ internalType: 'uint8', name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function'
  }
];

// Chainlink Price Feed adresai - Ethereum mainnet
const PRICE_FEEDS = {
  ethereum: '0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419', // ETH/USD
  binancecoin: '0x14e613AC84a31f709eadbdF89C6CC390fDc9540A', // BNB/USD
  solana: '0x4ffC43a60e009B551865A93d232E33Fce9f01507' // SOL/USD
};

interface PriceData {
  [key: string]: {
    usd: number;
  };
}

type LatestRoundData = [bigint, bigint, bigint, bigint, bigint];

export function useOraclePrices(cryptoIds: string[]) {
  const [prices, setPrices] = useState<PriceData | null>(null);

  // ETH/USD
  const { data: ethData, isError: ethError } = useReadContract({
    address: PRICE_FEEDS.ethereum as `0x${string}`,
    abi: priceFeedAbi,
    functionName: 'latestRoundData',
    chainId: 1 // Ethereum mainnet
  });
  
  const { data: ethDecimals } = useReadContract({
    address: PRICE_FEEDS.ethereum as `0x${string}`,
    abi: priceFeedAbi,
    functionName: 'decimals',
    chainId: 1
  });

  // BNB/USD
  const { data: bnbData, isError: bnbError } = useReadContract({
    address: PRICE_FEEDS.binancecoin as `0x${string}`,
    abi: priceFeedAbi,
    functionName: 'latestRoundData',
    chainId: 1 // Fetchable from Ethereum mainnet
  });
  
  const { data: bnbDecimals } = useReadContract({
    address: PRICE_FEEDS.binancecoin as `0x${string}`,
    abi: priceFeedAbi,
    functionName: 'decimals',
    chainId: 1
  });

  // SOL/USD
  const { data: solData, isError: solError } = useReadContract({
    address: PRICE_FEEDS.solana as `0x${string}`,
    abi: priceFeedAbi,
    functionName: 'latestRoundData',
    chainId: 1 // Fetchable from Ethereum mainnet
  });
  
  const { data: solDecimals } = useReadContract({
    address: PRICE_FEEDS.solana as `0x${string}`,
    abi: priceFeedAbi,
    functionName: 'decimals',
    chainId: 1
  });

  useEffect(() => {
    // Update prices when data changes
    if (ethData || bnbData || solData) {
      const updatedPrices: PriceData = {};
      
      if (ethData && ethDecimals) {
        const typedEthData = ethData as LatestRoundData;
        updatedPrices.ethereum = {
          usd: Number(formatUnits(typedEthData[1], Number(ethDecimals)))
        };
      } else {
        updatedPrices.ethereum = { usd: 2000 }; // Atsarginis variantas
      }
      
      if (bnbData && bnbDecimals) {
        const typedBnbData = bnbData as LatestRoundData;
        updatedPrices.binancecoin = {
          usd: Number(formatUnits(typedBnbData[1], Number(bnbDecimals)))
        };
      } else {
        updatedPrices.binancecoin = { usd: 600 }; // Atsarginis variantas
      }
      
      if (solData && solDecimals) {
        const typedSolData = solData as LatestRoundData;
        updatedPrices.solana = {
          usd: Number(formatUnits(typedSolData[1], Number(solDecimals)))
        };
      } else {
        updatedPrices.solana = { usd: 150 }; // Atsarginis variantas
      }
      
      setPrices(updatedPrices);
    } else if (ethError || bnbError || solError) {
      // Use fallback prices if errors
      setPrices({
        ethereum: { usd: 2000 },
        binancecoin: { usd: 600 },
        solana: { usd: 150 }
      });
    }
  }, [ethData, bnbData, solData, ethDecimals, bnbDecimals, solDecimals, ethError, bnbError, solError]);

  return prices;
} 